import store from '../store'
import router from '../router'

function getRuntimeOriginParts() {
    const protocol = window.location.protocol || 'http:'
    const hostname = window.location.hostname || ''

    // When packaged as an exe, the UI is often loaded from `file://...`.
    // In that case, backend is still expected to be reachable over HTTP on localhost.
    const resolvedProtocol = protocol === 'file:' ? 'http:' : protocol
    const resolvedHostname = hostname || 'localhost'

    return { protocol: resolvedProtocol, hostname: resolvedHostname }
}

export function resolveBackendOrigin() {
    if (window.location.protocol === 'file:') {
        return 'http://localhost:3004'
    }

    const { protocol, hostname } = getRuntimeOriginParts()
    const pagePort = window.location.port

    // UI served on standard HTTP(S) without an explicit port (reverse proxy).
    if (!pagePort || pagePort === '80' || pagePort === '443') {
        return `${protocol}//${hostname}`
    }

    if (pagePort === '3004') {
        return `${protocol}//${hostname}:3004`
    }

    return `${protocol}//${hostname}:3004`
}

export function resolveWsOrigin() {
    if (window.location.protocol === 'file:') {
        return 'ws://localhost:3004'
    }

    const { hostname } = getRuntimeOriginParts()
    const pagePort = window.location.port
    const pageProtocol = window.location.protocol

    // UI served directly from the API port — WS is on the same Node HTTP server.
    if (pagePort === '3004') {
        const wsProtocol = pageProtocol === 'https:' ? 'wss:' : 'ws:'
        return `${wsProtocol}//${hostname}:3004`
    }

    // HTTPS UI on 443: browsers require wss:// same-origin (ws://host:3004 is mixed-content blocked).
    // This needs nginx to forward Upgrade/Connection to Node :3004 — see
    // server/API/deploy/nginx-websocket-upgrade.conf
    if (pageProtocol === 'https:' && (!pagePort || pagePort === '443')) {
        return `wss://${hostname}`
    }

    // HTTP reverse proxy / other ports: talk to the API WebSocket port directly.
    return `ws://${hostname}:3004`
}

const backendOrigin = resolveBackendOrigin()

export const API_BASE_URL = `${backendOrigin}/api`
export const UPLOADS_BASE_URL = `${backendOrigin}/uploads`
export const WS_BASE_URL = resolveWsOrigin()

export function normalizeUploadValue(value) {
    const raw = String(value || '').trim()
    if (!raw) return ''
    if (raw.startsWith('blob:') || raw.startsWith('data:')) {
        return raw
    }

    const uploadsMatch = raw.match(/\/uploads\/([^?#]+)/i)
    if (uploadsMatch?.[1]) {
        return uploadsMatch[1]
    }

    return raw.replace(/^\/?uploads\//i, '')
}

export function resolveCompanyLogoUrl(value) {
    const raw = normalizeUploadValue(value)
    if (!raw) return ''
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('blob:') || raw.startsWith('data:')) {
        return raw
    }
    return `${UPLOADS_BASE_URL}/${raw}`
}

function decodeJwtPayload(token) {
    if (!token || typeof token !== 'string') return null
    const parts = token.split('.')
    if (parts.length < 2) return null
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        )
        return JSON.parse(json)
    } catch {
        return null
    }
}

let serverTimeOffsetMs = 0
let lastServerTimeSyncAt = 0
const SERVER_TIME_SYNC_INTERVAL_MS = 5 * 60 * 1000

/**
 * Align client-side JWT checks with backend clock so devices with incorrect
 * local time do not trigger excessive token refresh / DB load.
 */
export async function syncServerTime({ force = false } = {}) {
    const now = Date.now()
    if (!force && lastServerTimeSyncAt && (now - lastServerTimeSyncAt) < SERVER_TIME_SYNC_INTERVAL_MS) {
        return serverTimeOffsetMs
    }

    const started = Date.now()
    try {
        const res = await fetch(`${API_BASE_URL}/health`)
        if (!res.ok) return serverTimeOffsetMs

        const data = await res.json().catch(() => ({}))
        const serverTime = Number(data?.serverTime)
        if (!Number.isFinite(serverTime)) return serverTimeOffsetMs

        const roundTripMs = Date.now() - started
        const estimatedServerNow = serverTime + Math.floor(roundTripMs / 2)
        serverTimeOffsetMs = estimatedServerNow - Date.now()
        lastServerTimeSyncAt = Date.now()
    } catch {
        // Keep the previous offset when health is unreachable.
    }

    return serverTimeOffsetMs
}

export function getServerNowMs() {
    return Date.now() + serverTimeOffsetMs
}

function getServerNowSec() {
    return Math.floor(getServerNowMs() / 1000)
}

function shouldRefreshToken(token) {
    const payload = decodeJwtPayload(token)
    const exp = Number(payload?.exp || 0)
    if (!exp) return false
    const nowSec = getServerNowSec()
    const REFRESH_WINDOW_SEC = 2 * 60
    return exp - nowSec <= REFRESH_WINDOW_SEC
}

let refreshInFlight = null

async function ensureFreshToken() {
    if (!store.state.token || !shouldRefreshToken(store.state.token)) {
        return true
    }

    if (!refreshInFlight) {
        refreshInFlight = store.dispatch('refreshToken').finally(() => {
            refreshInFlight = null
        })
    }

    const refreshed = await refreshInFlight
    if (!refreshed) {
        await handleSessionExpired()
        throw new Error('Session expired')
    }
    return true
}

/**
 * Centralized session expiry handler
 */
export const handleSessionExpired = async () => {
    try {
        const source = store.getters.loginSource
        if (store && store.dispatch) await store.dispatch('logout')
        
        const routeName = source === 'waiter' ? 'waiter_login' : 'login'
        router.push({ name: routeName, query: { session: 'expired' } })
    } catch (err) {
        console.error('Error in handleSessionExpired:', err)
        router.push({ name: 'login', query: { session: 'expired' } })
    }
}

export const handleForcedLogout = async (reason = 'expired', message = '') => {
    try {
        const source = store.getters.loginSource
        if (store && store.dispatch) await store.dispatch('logout')
        const routeName = source === 'waiter' ? 'waiter_login' : 'login'
        const query = { session: 'expired' }
        if (reason === 'clock' || reason === 'subscription') {
            query.reason = reason
        }
        if (message) query.message = message
        router.push({ name: routeName, query })
    } catch (err) {
        console.error('Error in handleForcedLogout:', err)
        const query = { session: 'expired' }
        if (reason === 'clock' || reason === 'subscription') query.reason = reason
        if (message) query.message = message
        router.push({ name: 'login', query })
    }
}

function isPublicAuthEndpoint(cleanEndpoint) {
    return (
        cleanEndpoint.startsWith('/users/login') ||
        cleanEndpoint.startsWith('/users/activate-subscription') ||
        cleanEndpoint.startsWith('/users/waiter') ||
        cleanEndpoint.startsWith('/orders/finished') ||
        cleanEndpoint.startsWith('/orders/public/') ||
        cleanEndpoint.startsWith('/super-admin/login')
    )
}

export function isApiAuthorized() {
    return !!store.state.token
}

/**
 * Enhanced fetch wrapper that handles authorization and session expiry
 */
export const apiFetch = async (endpoint, options = {}) => {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${cleanEndpoint}`

    if (!store.state.token && !isPublicAuthEndpoint(cleanEndpoint)) {
        throw new Error('Not authenticated')
    }

    // Check inactivity expiry before making any request
    try {
        const last = parseInt(localStorage.getItem('Mina_last_activity') || '0', 10)
        const INACTIVITY_LIMIT_MS = 30 * 60 * 1000 // 30 minutes
        if (last && Date.now() - last > INACTIVITY_LIMIT_MS) {
            // session expired due to inactivity
            await handleSessionExpired()
            throw new Error('Session expired due to inactivity')
        }
        // update last activity for this request
        localStorage.setItem('Mina_last_activity', String(Date.now()))
    } catch (err) {
        // ignore storage errors
    }

    // Prepare headers
    const headers = {
        ...options.headers
    }

    // Only set Content-Type if not FormData and not already set
    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    // Proactively refresh token shortly before expiry (using server-adjusted clock).
    if (store.state.token) {
        await syncServerTime()
        await ensureFreshToken()
    }

    // Inject token if available
    if (store.state.token) {
        headers['Authorization'] = `Bearer ${store.state.token}`
    }

    const config = {
        ...options,
        headers
    }

    try {
        const response = await fetch(url, config)

        // Subscription / payment period ended (403)
        if (response.status === 403) {
            const isAuthRoute =
                url.includes('/users/login') ||
                url.includes('/users/activate-subscription') ||
                url.includes('/super-admin/login')
            if (!isAuthRoute) {
                try {
                    const body = await response.clone().json()
                    if (body?.code === 'SUBSCRIPTION_EXPIRED' || body?.forceLogout === true) {
                        const msg =
                            body?.message ||
                            'Your subscription period has ended. Please contact your administrator to renew.'
                        await handleForcedLogout('subscription', msg)
                        throw new Error(msg)
                    }
                } catch (parseErr) {
                    if (parseErr?.message?.includes('subscription') || parseErr?.message?.includes('Subscription')) {
                        throw parseErr
                    }
                }
            }
        }

        // Handle session expiry (401 Unauthorized) with one refresh retry
        if (response.status === 401) {
            const isAuthRoute = url.includes('/super-admin/login') || url.includes('/users/login') || url.includes('/users/refresh')
            let forcedLogoutMessage = ''
            let forcedLogoutReason = 'expired'
            try {
                const body = await response.clone().json()
                if (body?.code === 'SESSION_INVALIDATED' || (body?.forceLogout === true && body?.code !== 'SERVER_TIME_INVALID')) {
                    forcedLogoutMessage = body?.message || 'Session expired. Please log in again.'
                    forcedLogoutReason = 'expired'
                } else if (
                    body?.code === 'SERVER_TIME_INVALID' ||
                    body?.message === 'Date and time issue, please check your server date and time.'
                ) {
                    forcedLogoutMessage = 'Date and time issue, please check your server date and time.'
                    forcedLogoutReason = 'clock'
                }
            } catch (_) {
                // ignore non-json 401 body
            }

            if (!isAuthRoute && forcedLogoutMessage) {
                await handleForcedLogout(forcedLogoutReason, forcedLogoutMessage)
                throw new Error(forcedLogoutMessage)
            }

            // Don't trigger for login attempts (super-admin or generic user)
            if (!isAuthRoute) {
                const refreshed = await store.dispatch('refreshToken')
                if (refreshed) {
                    const retryHeaders = {
                        ...headers,
                        ...(store.state.token ? { Authorization: `Bearer ${store.state.token}` } : {})
                    }
                    return await fetch(url, { ...config, headers: retryHeaders })
                }

                await handleForcedLogout('expired')
                throw new Error('Session expired')
            }
        }

        return response
    } catch (error) {
        if (error.message === 'Session expired' || error.message === 'Not authenticated') throw error
        console.error(`API Fetch Error [${url}]:`, error)
        throw error
    }
}

/**
 * Renew subscription with a license key (no JWT stored — credentials verified server-side only).
 */
export async function activateSubscription({ username, password, activationKey }) {
    const response = await fetch(`${API_BASE_URL}/users/activate-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            username: String(username || '').trim().toLowerCase(),
            password: String(password || '').trim().toLowerCase(),
            activationKey: String(activationKey || '').trim()
        })
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
        const err = new Error(data.message || 'Activation failed')
        if (data.code) err.code = data.code
        throw err
    }
    return data
}

/**
 * Renew subscription while logged in (extends by 1 year + remaining due days).
 */
export async function activateSubscriptionInApp({ activationKey }) {
    const response = await apiFetch('/users/activate-subscription-in-app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            activationKey: String(activationKey || '').trim()
        })
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
        const err = new Error(data.message || 'Activation failed')
        if (data.code) err.code = data.code
        throw err
    }
    return data
}
